import { AlertTriangle, RotateCcw } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { EmptyState } from '../components/EmptyState';
import { SectionCard } from '../components/SectionCard';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../hooks/useActionDialog';
import {
  formatArtifactSummary,
  formatRelativeDate,
} from '../utils/formatters';

type RollbackPageProps = {
  openDialog: UseActionDialogReturn['openDialog'];
};

// Recovery history is scoped to one exact release channel; never substitute
// another app version or environment when finding a restore point.
function RollbackPage({ openDialog }: RollbackPageProps) {
  const {
    sortedPromotions,
    artifactById,
    releaseAppById,
    releaseAppVersionById,
    channels,
    selectNav,
  } = useWorkspace();
  const [view, setView] = useState<'recoverable' | 'all'>('recoverable');

  const targets = useMemo(
    () => channels.map((channel) => {
      const version = channel.releaseAppVersionId
        ? releaseAppVersionById.get(channel.releaseAppVersionId)
        : undefined;
      const app = channel.releaseAppId
        ? releaseAppById.get(channel.releaseAppId)
        : version
          ? releaseAppById.get(version.releaseAppId)
          : undefined;
      const exactHistory = sortedPromotions.filter(
        (promotion) => promotion.releaseChannelId === channel.releaseChannelId
      );
      const currentBundle = channel.releaseArtifactId
        ? artifactById.get(channel.releaseArtifactId)
        : undefined;
      const previousPromotion = exactHistory[1];
      const previousBundle = previousPromotion
        ? artifactById.get(previousPromotion.releaseArtifactId)
        : undefined;

      return {
        channel,
        version,
        app,
        exactHistory,
        currentBundle,
        previousBundle,
        canRestore: Boolean(previousPromotion),
        isLegacy: !app || !version,
      };
    }),
    [artifactById, channels, releaseAppById, releaseAppVersionById, sortedPromotions]
  );
  const recoverableTargets = targets.filter((target) => target.canRestore);
  const servingTargets = targets.filter(
    (target) => target.channel.isActive && target.currentBundle
  );
  const legacyTargets = targets.filter((target) => target.isLegacy);
  const visibleTargets = view === 'recoverable' ? recoverableTargets : targets;

  return (
    <div className="recoveryPage">
      <section className="recoverySummaryStrip" aria-label="Recovery coverage">
        <div className={recoverableTargets.length > 0 ? 'is-healthy' : 'is-warning'}>
          <span>Recoverable now</span>
          <strong>{recoverableTargets.length}</strong>
          <small>targets with a previous bundle</small>
        </div>
        <div>
          <span>Serving targets</span>
          <strong>{servingTargets.length}</strong>
          <small>currently assigned a bundle</small>
        </div>
        <div className={servingTargets.length > recoverableTargets.length ? 'is-warning' : 'is-healthy'}>
          <span>Without history</span>
          <strong>{Math.max(0, servingTargets.length - recoverableTargets.length)}</strong>
          <small>need one more deployment</small>
        </div>
        <div className={legacyTargets.length > 0 ? 'is-blocked' : 'is-healthy'}>
          <span>Legacy targets</span>
          <strong>{legacyTargets.length}</strong>
          <small>not mapped to an app version</small>
        </div>
      </section>

      <SectionCard
        className="recoveryTargetsPanel"
        label="Bundle recovery"
        title="Restore a previous bundle"
        description="Recovery changes one app version in one environment. A previous bundle must exist for that target."
        action={
          <div className="recoveryViewToggle" aria-label="Recovery target view">
            <button type="button" className={view === 'recoverable' ? 'is-active' : ''} onClick={() => setView('recoverable')}>
              Recoverable <span>{recoverableTargets.length}</span>
            </button>
            <button type="button" className={view === 'all' ? 'is-active' : ''} onClick={() => setView('all')}>
              All targets <span>{targets.length}</span>
            </button>
          </div>
        }
      >
        {visibleTargets.length > 0 ? (
          <div className="releaseTableWrap" role="region" aria-label="Recovery targets table" tabIndex={0}>
            <table className="releaseTable releaseTableRecovery">
              <thead>
                <tr>
                  <th>App / version</th>
                  <th>Environment</th>
                  <th>Currently running</th>
                  <th>Restore point</th>
                  <th>Last changed</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {visibleTargets.map(({ channel, version, app, exactHistory, currentBundle, previousBundle, canRestore, isLegacy }) => (
                  <tr key={channel.releaseChannelId} className={canRestore ? 'is-recoverable' : ''}>
                    <td>
                      <div className="releaseIdentity">
                        <strong>{app?.name || 'App not recorded'} · {version?.version || channel.channel}</strong>
                        <span>{app?.platform?.toUpperCase() || 'Legacy target'}{isLegacy ? ' · mapping required' : ' target'}</span>
                      </div>
                    </td>
                    <td><span className={`envPill env-${channel.environment}`}>{channel.environment}</span></td>
                    <td>
                      <div className="releaseIdentity">
                        <strong>{currentBundle ? formatArtifactSummary(currentBundle) : 'No bundle assigned'}</strong>
                        <span>{channel.isActive ? 'Active target' : 'Paused target'}</span>
                      </div>
                    </td>
                    <td>
                      <div className="releaseIdentity">
                        <strong>{previousBundle ? formatArtifactSummary(previousBundle) : 'No restore point'}</strong>
                        <span>{canRestore ? 'Previous bundle available' : 'Deploy again to create a restore point'}</span>
                      </div>
                    </td>
                    <td>{formatRelativeDate(exactHistory[0]?.created || channel.updated)}</td>
                    <td className="releaseTableAction">
                      {canRestore ? (
                        <button
                          type="button"
                          className="secondaryButton"
                          onClick={() => openDialog({ kind: 'rollback', releaseChannelId: channel.releaseChannelId })}
                        >
                          <RotateCcw size={14} /> Restore
                        </button>
                      ) : <span className="recoveryUnavailable">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={<AlertTriangle size={18} />}
            title="No restore points yet"
            description={servingTargets.length > 0
              ? `${servingTargets.length} serving target${servingTargets.length === 1 ? '' : 's'} need another deployment before Cindro can restore the previous bundle.`
              : 'Deploy a bundle to an app version before Recovery can retain its history.'}
            action={
              <div className="emptyStateActions">
                <button type="button" className="primaryButton" onClick={() => selectNav('promotions')}>Deploy update</button>
                <button type="button" className="secondaryButton" onClick={() => setView('all')}>Inspect all targets</button>
              </div>
            }
          />
        )}
      </SectionCard>
    </div>
  );
}

export { RollbackPage };
